import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField, IconButton, InputAdornment } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles({
  root: {
    marginTop: '8px',
    marginBottom: '8px',
    minWidth: '220px',
  },
});

export interface DebouncedInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  debounceMs?: number;
  resettable?: boolean;
  disabled?: boolean;
  error?: string;
  fullWidth?: boolean;
  className?: string;
  'data-cy'?: string;
}

export const DebouncedInput: React.FC<DebouncedInputProps> = ({
  label,
  value,
  onChange,
  debounceMs = 300,
  resettable = false,
  disabled = false,
  error,
  fullWidth = false,
  className,
  'data-cy': dataCy,
}) => {
  const classes = useStyles();

  const [inputValue, setInputValue] = useState(value);
  const isFirstRender = useRef(true);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  useEffect(() => {
    // Skip debounce on first render to avoid initial unnecessary callback
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      if (inputValue !== value) {
        onChange(inputValue);
      }
    }, debounceMs);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [inputValue, debounceMs]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [],
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    // Immediately trigger onChange for clear action
    onChange('');
  }, [onChange]);

  return (
    <TextField
      className={`${classes.root} ${className || ''}`}
      label={label}
      value={inputValue}
      onChange={handleChange}
      variant="filled"
      margin="dense"
      disabled={disabled}
      error={!!error}
      helperText={error}
      fullWidth={fullWidth}
      data-cy={dataCy}
      InputProps={{
        endAdornment:
          resettable && inputValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                disabled={disabled}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
      }}
    />
  );
};

export default DebouncedInput;
